// ITER289 — Premium Loyalty presentational components.
// ITER294 — Premium voucher wallet redesign.
// All pure — the parent (GuestLoyaltyWallet) owns state and API calls.
// Consumes CSS variables mounted by <GuestLoyaltyThemeProvider>.
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { formatBenefitValue } from './benefitFormatter';
import { Ticket, QrCode, Clock, User, Home as HomeIcon,
         Copy, Share2, CheckCircle2, Sparkles, Lock,
         Gift, Percent, IndianRupee, UtensilsCrossed, Coffee,
         Wine, MapPin, FileText } from 'lucide-react';

/* ============================================================
   Hero — restaurant identity + welcome
   ============================================================ */
export function LoyaltyHero({ outletName, welcomeMessage, memberFirstName, programName, logoUrl }) {
  return (
    <div className="flex flex-col items-center text-center pt-10 pb-6 px-6 space-y-4"
         data-testid="loyalty-hero">
      {logoUrl ? (
        <div className="w-20 h-20 rounded-full overflow-hidden border flex items-center justify-center bg-white"
             style={{ borderColor: 'var(--border-subtle)', boxShadow: 'var(--shadow-sm)' }}>
          <img src={logoUrl} alt={outletName} className="w-full h-full object-cover" />
        </div>
      ) : (
        <div className="w-16 h-16 rounded-full flex items-center justify-center"
             style={{ background: 'var(--gold-soft)', color: 'var(--primary)' }}>
          <Sparkles className="w-6 h-6" />
        </div>
      )}
      <span
        className="inline-flex items-center px-3.5 py-1.5 rounded-full text-[10px] font-semibold uppercase tracking-[0.22em] border"
        style={{
          background: 'rgba(255, 255, 255, 0.06)',
          borderColor: 'rgba(255, 255, 255, 0.14)',
          color: 'var(--gold)',
        }}
        data-testid="loyalty-program-badge"
      >
        {programName || 'Privilege Membership'}
      </span>
      <h1 className="font-serif text-3xl md:text-4xl leading-tight tracking-tight"
          style={{ color: 'var(--ink)' }}
          data-testid="loyalty-hero-outlet">
        {outletName}
      </h1>
      {(memberFirstName || welcomeMessage) && (
        <p className="max-w-sm text-base leading-relaxed"
           style={{ color: 'var(--ink-muted)' }}>
          {memberFirstName
            ? <>Welcome back, <span className="font-medium" style={{ color: 'var(--ink)' }}>{memberFirstName}</span>.</>
            : (welcomeMessage || '')}
        </p>
      )}
    </div>
  );
}

/* ============================================================
   Membership Card — the hero component
   ============================================================ */
export function MembershipCard({ card, memberName, memberIdPublic, tier, onOpenQr }) {
  const validThrough = card.expires_at ? String(card.expires_at).slice(0, 10) : '';
  return (
    <motion.div
      whileTap={{ scale: 0.985 }}
      transition={{ type: 'spring', stiffness: 400, damping: 25 }}
      className="relative w-full aspect-[1.58] rounded-[20px] overflow-hidden text-white
                 flex flex-col justify-between"
      style={{
        background: 'var(--gradient-card)',
        // ITER305-refine · Physical premium card feel — light gold
        // edge + inset gold glow so the card reads as a metal card,
        // not a flat rectangle. 2px (not 1.5px) so it survives DPR=1
        // rounding.
        border: '2px solid rgba(201, 162, 39, 0.60)',
        boxShadow:
          'inset 0 0 14px rgba(201,162,39,0.18),' +
          '0 12px 32px rgba(0,0,0,0.35),' +
          '0 2px 6px rgba(0,0,0,0.25)',
        padding: '22px',
      }}
      data-testid="membership-card"
    >
      {/* ITER306.1 · Subtle gold shimmer.
           A diagonal, low-opacity gold gloss sweeps across the card
           once every ~5 seconds. Pure CSS keyframe animation — no JS
           timers, no bundle cost, and paused entirely when the user
           has `prefers-reduced-motion: reduce`. Applied only to the
           membership card (this component is only mounted on Home).
           Scoped inside data-testid=membership-card so it never leaks
           to voucher cards or counter chips. */}
      <style>{`
        @keyframes rp-card-shimmer {
          0%   { transform: translateX(-140%) skewX(-16deg); opacity: 0; }
          15%  { opacity: 0.18; }
          65%  { opacity: 0.18; }
          80%  { opacity: 0; }
          100% { transform: translateX(140%) skewX(-16deg); opacity: 0; }
        }
        [data-testid="membership-card"] .rp-shimmer {
          position: absolute;
          top: 0;
          bottom: 0;
          left: 0;
          width: 45%;
          pointer-events: none;
          background: linear-gradient(
            90deg,
            transparent 0%,
            rgba(255, 232, 155, 0.55) 45%,
            rgba(255, 246, 214, 0.85) 50%,
            rgba(255, 232, 155, 0.55) 55%,
            transparent 100%
          );
          mix-blend-mode: screen;
          filter: blur(0.5px);
          animation: rp-card-shimmer 5s ease-in-out infinite;
          animation-delay: 1.5s;
        }
        @media (prefers-reduced-motion: reduce) {
          [data-testid="membership-card"] .rp-shimmer { display: none; }
        }
      `}</style>
      <div aria-hidden="true" className="rp-shimmer" />

      {/* Subtle gold texture pattern */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(circle at 20% 10%, rgba(201,163,78,0.14) 0, transparent 40%),' +
            'radial-gradient(circle at 90% 80%, rgba(201,163,78,0.08) 0, transparent 45%)',
        }}
      />
      {/* Top row: tier label + member id badge + QR trigger.
          Member-id sits inline with the tier chip (right-side of the
          left block) instead of a top-center watermark — fixes the
          iPhone-SE overlap that testing agent flagged. */}
      <div className="relative z-10 flex justify-between items-start gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-[0.22em] opacity-70 font-medium">
              {(tier || 'Privilege') + ' Member'}
            </span>
            <span
              className="text-[9px] uppercase tracking-[0.24em] font-medium px-1.5 py-0.5 rounded"
              style={{
                background: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.16)',
                opacity: 0.85,
              }}
              data-testid="membership-member-id"
            >
              {memberIdPublic}
            </span>
          </div>
          <div className="font-serif italic text-lg sm:text-xl mt-1 truncate"
               style={{ color: 'var(--gold)' }}
               data-testid="membership-tier">
            {card.kit_name || 'Signature'}
          </div>
        </div>
        <button
          onClick={onOpenQr}
          aria-label="Show membership QR"
          data-testid="membership-qr-trigger"
          className="p-2.5 rounded-full transition-colors flex-shrink-0"
          style={{
            background: 'rgba(255,255,255,0.10)',
            border: '1px solid rgba(255,255,255,0.2)',
          }}
        >
          <QrCode className="w-4 h-4" />
        </button>
      </div>

      {/* Middle: card number — dominant.
          Auto-fit responsive tracking so `NNNN-NNNN-NNNN` stays on one
          line even at 360px viewport widths. */}
      <div className="relative z-10 text-center py-2">
        <div
          className="font-mono font-medium text-white/95"
          style={{
            fontSize: 'clamp(0.95rem, 4.4vw, 1.25rem)',
            letterSpacing: 'clamp(0.14em, 1.2vw, 0.28em)',
          }}
          data-testid="membership-card-number"
        >
          {card.card_number}
        </div>
      </div>

      {/* Bottom row: member name + valid through */}
      <div className="relative z-10 flex justify-between items-end gap-3">
        <div className="min-w-0">
          <div className="text-[9px] uppercase tracking-[0.22em] opacity-60 mb-1">Member Name</div>
          <div className="font-sans text-sm sm:text-base font-medium truncate"
               data-testid="membership-name">
            {memberName}
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="text-[9px] uppercase tracking-[0.22em] opacity-60 mb-1">Valid Through</div>
          <div className="font-sans text-sm sm:text-base font-medium"
               data-testid="membership-valid-through">
            {validThrough || '—'}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

/* ============================================================
   ITER294 — Benefit-type → icon + label helper.
   Drives the "smart template": each benefit gets its own hero
   glyph + short label ribbon so a QSR percent-off card never
   looks like a spa gift certificate.
   ============================================================ */
const BENEFIT_META = {
  percent_discount:    { Icon: Percent,          label: '% Off' },
  flat_discount:       { Icon: IndianRupee,      label: 'Flat Off' },
  complimentary_item:  { Icon: Gift,             label: 'Complimentary' },
  buy_one_get_one:     { Icon: UtensilsCrossed,  label: 'BOGO' },
  cashback:            { Icon: IndianRupee,      label: 'Cashback' },
  custom:              { Icon: Sparkles,         label: 'Privilege' },
};
function benefitMeta(type) {
  return BENEFIT_META[type] || BENEFIT_META.custom;
}
function benefitHeadline(type, value, voucher) {
  // ITER305-refine · delegate to the shared formatter so ₹ is only
  // ever added for genuinely monetary benefit types.
  return formatBenefitValue(voucher || { benefit_type: type, benefit_value: value },
                             type === 'cashback' ? 'back' :
                             (type === 'flat_discount' || type === 'percent_discount')
                               ? 'off' : 'value') || null;
}

/* ============================================================
   ITER294 — Elegant fallback artwork (used when a voucher has
   no admin-uploaded hero image). Deterministic per voucher id so
   the same voucher always looks the same, but two vouchers in the
   same wallet look distinct. Pure SVG — no external assets, prints
   perfectly, no CDN dependency.
   ============================================================ */
function FallbackHeroArt({ seed = '', Icon = Sparkles }) {
  // Tiny FNV-1a-style hash to pick a rotation deterministically.
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = ((h * 31) + seed.charCodeAt(i)) & 0xffff;
  const rot = (h % 12) * 30;
  return (
    <div className="absolute inset-0 overflow-hidden"
         style={{ background: 'var(--gradient-hero)' }} aria-hidden="true">
      {/* Radial gold glow */}
      <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full"
           style={{ background: 'radial-gradient(circle, var(--accent-soft) 0%, transparent 70%)',
                    opacity: 0.35, transform: `rotate(${rot}deg)` }} />
      {/* Rotating luxe pattern */}
      <svg className="absolute inset-0 w-full h-full opacity-30"
           viewBox="0 0 200 200" preserveAspectRatio="xMidYMid slice">
        <defs>
          <pattern id={`p-${h}`} width="28" height="28" patternUnits="userSpaceOnUse"
                    patternTransform={`rotate(${rot})`}>
            <circle cx="2" cy="2" r="1" fill="var(--accent)" opacity="0.4" />
            <path d="M0 14 L28 14 M14 0 L14 28" stroke="var(--accent)"
                   strokeWidth="0.4" opacity="0.25" />
          </pattern>
        </defs>
        <rect width="200" height="200" fill={`url(#p-${h})`} />
      </svg>
      {/* Center emblem */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-16 h-16 rounded-full flex items-center justify-center"
             style={{ background: 'rgba(255,255,255,0.10)',
                      border: '1px solid rgba(255,255,255,0.20)',
                      backdropFilter: 'blur(4px)' }}>
          <Icon className="w-7 h-7" style={{ color: 'var(--on-primary)' }} />
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   Voucher Card + reveal experience
   ============================================================ */
const STATUS_META = {
  unused:   { label: 'Available',  fg: 'var(--status-unused)',   bg: 'var(--status-unused-bg)' },
  reserved: { label: 'Reserved',   fg: 'var(--status-reserved)', bg: 'var(--status-reserved-bg)' },
  redeemed: { label: 'Redeemed',   fg: 'var(--status-redeemed)', bg: 'var(--status-redeemed-bg)' },
  expired:  { label: 'Expired',    fg: 'var(--status-expired)',  bg: 'var(--status-expired-bg)' },
};

export function VoucherStatusPill({ status }) {
  const s = STATUS_META[status] || STATUS_META.unused;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px]
                 font-semibold uppercase tracking-[0.15em]"
      style={{ background: s.bg, color: s.fg }}
      data-testid={`voucher-status-${status}`}
    >
      <span className="w-1 h-1 rounded-full" style={{ background: s.fg }} />
      {s.label}
    </span>
  );
}

/**
 * VoucherCard — ITER294 premium redesign.
 *
 * Anatomy (top → bottom):
 *   1. Hero image band  (admin-uploaded, or elegant SVG fallback)
 *   2. Brand strip      (logo + brand name)
 *   3. Offer headline   (dominant type)
 *   4. Description
 *   5. Meta row         (outlet · valid through)
 *   6. Reveal / code / redeemed state
 *
 * Every colour resolves via CSS variables from the ThemeProvider.
 * `brand` is an optional { name, logoUrl, outletName } — when absent
 * the card falls back to voucher_type_name + outlet_id.
 */
export function VoucherCard({ v, onReveal, revealBusy, revealedCode,
                              onShare, brand }) {
  const [copied, setCopied] = useState(false);
  const [heroBroken, setHeroBroken] = useState(false);
  const canReveal = v.status === 'unused' && !revealedCode;
  const isTerminal = v.status === 'redeemed' || v.status === 'expired';

  const copy = async () => {
    if (!revealedCode) return;
    try {
      await navigator.clipboard.writeText(revealedCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };

  const valid = v.valid_until ? String(v.valid_until).slice(0, 10) : '';
  const meta      = benefitMeta(v.benefit_type);
  const headline  = benefitHeadline(v.benefit_type, v.benefit_value, v);
  const heroImg   = (!heroBroken && v.hero_image_url) ? v.hero_image_url : null;
  const brandName = brand?.name || brand?.outletName || '';
  const outletName = brand?.outletName || '';
  const logoUrl   = brand?.logoUrl || '';
  const description = v.description || '';

  return (
    <motion.article
      whileTap={{ scale: 0.995 }}
      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
      className={`relative w-full rounded-[22px] border overflow-hidden
                  ${isTerminal ? 'opacity-70' : ''}`}
      style={{
        background: 'var(--canvas)',
        borderColor: 'var(--border-default)',
        boxShadow: 'var(--shadow-lg)',
      }}
      data-testid={`voucher-${v.id}`}
    >
      {/* ═══════════════ 1. Hero band ═══════════════ */}
      <div className="relative w-full aspect-[16/9]"
           data-testid={`voucher-hero-${v.id}`}>
        {heroImg ? (
          <>
            <img src={heroImg} alt=""
                 className="absolute inset-0 w-full h-full object-cover"
                 onError={() => setHeroBroken(true)} />
            {/* Gradient scrim so brand strip + headline stay legible */}
            <div className="absolute inset-0"
                 style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.10) 0%, rgba(0,0,0,0.55) 100%)' }} />
          </>
        ) : (
          <FallbackHeroArt seed={v.id || v.voucher_type_id || ''} Icon={meta.Icon} />
        )}

        {/* Top-row: brand strip (left) + status pill (right) */}
        <div className="absolute top-0 left-0 right-0 flex items-center justify-between p-4">
          <div className="flex items-center gap-2 min-w-0">
            {logoUrl ? (
              <div className="w-8 h-8 rounded-full overflow-hidden bg-white flex-shrink-0"
                   style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.25)' }}>
                <img src={logoUrl} alt="" className="w-full h-full object-cover" />
              </div>
            ) : null}
            {brandName && (
              <div className="text-[10px] uppercase tracking-[0.22em] font-semibold truncate"
                   style={{ color: 'rgba(255,255,255,0.92)',
                            textShadow: '0 1px 6px rgba(0,0,0,0.4)' }}
                   data-testid={`voucher-brand-${v.id}`}>
                {brandName}
              </div>
            )}
          </div>
          <VoucherStatusPill status={v.status} />
        </div>

        {/* Bottom-row: benefit ribbon (headline value or type) */}
        <div className="absolute bottom-3 left-4 right-4 flex items-end justify-between gap-3">
          <div className="min-w-0">
            {headline ? (
              <div className="font-serif text-3xl md:text-4xl font-medium tracking-tight leading-none"
                   style={{ color: '#FFFFFF',
                            textShadow: '0 2px 12px rgba(0,0,0,0.35)' }}
                   data-testid={`voucher-headline-${v.id}`}>
                {headline}
              </div>
            ) : (
              <div className="font-serif text-2xl text-white leading-none"
                   style={{ textShadow: '0 2px 12px rgba(0,0,0,0.35)' }}>
                {meta.label}
              </div>
            )}
          </div>
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full
                          text-[9px] uppercase tracking-[0.18em] font-semibold"
               style={{ background: 'rgba(255,255,255,0.18)',
                        color: '#FFFFFF',
                        border: '1px solid rgba(255,255,255,0.25)',
                        backdropFilter: 'blur(6px)' }}>
            <meta.Icon className="w-3 h-3" />
            {meta.label}
          </div>
        </div>
      </div>

      {/* ═══════════════ 2. Body ═══════════════ */}
      <div className="relative p-5 pt-4">
        {/* Perforation strip — a subtle dashed divider that echoes a
             printed gift certificate's tear line. Purely decorative. */}
        <div className="absolute left-0 right-0 -top-[6px] flex items-center justify-between px-1"
             aria-hidden="true">
          <div className="w-3 h-3 rounded-full"
               style={{ background: 'var(--surface)', border: '1px solid var(--border-default)' }} />
          <div className="flex-1 mx-2 border-t border-dashed"
               style={{ borderColor: 'var(--border-default)' }} />
          <div className="w-3 h-3 rounded-full"
               style={{ background: 'var(--surface)', border: '1px solid var(--border-default)' }} />
        </div>

        <div className="font-serif text-xl leading-snug"
             style={{ color: 'var(--ink)' }}
             data-testid={`voucher-title-${v.id}`}>
          {v.voucher_type_name}
        </div>

        {description && (
          <p className="mt-1.5 font-sans text-sm leading-relaxed"
             style={{ color: 'var(--ink-muted)' }}>
            {description}
          </p>
        )}

        {/* Meta row — outlet + expiry, ITER294 explicit */}
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5
                        text-[11px] font-medium"
             style={{ color: 'var(--ink-muted)' }}>
          {outletName && (
            <span className="inline-flex items-center gap-1.5"
                  data-testid={`voucher-outlet-${v.id}`}>
              <MapPin className="w-3 h-3" style={{ color: 'var(--accent)' }} />
              Valid at <span style={{ color: 'var(--ink)' }}>{outletName}</span>
            </span>
          )}
          {valid && (
            <span className="inline-flex items-center gap-1.5">
              <Clock className="w-3 h-3" style={{ color: 'var(--accent)' }} />
              Through <span style={{ color: 'var(--ink)' }}>{valid}</span>
            </span>
          )}
        </div>

        {/* ═══════════════ 3. Reveal / code / terminal ═══════════════ */}
        <div className="mt-5 pt-4 border-t" style={{ borderColor: 'var(--border-subtle)' }}>
          <AnimatePresence mode="wait">
            {revealedCode ? (
              <motion.div
                key="revealed"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.4, ease: [0.23, 1, 0.32, 1] }}
                className="flex flex-col items-center space-y-3"
                data-testid={`revealed-panel-${v.id}`}
              >
                <div className="text-[10px] uppercase tracking-[0.22em] font-semibold"
                     style={{ color: 'var(--ink-muted)' }}>
                  Redemption Code
                </div>
                <div
                  className="w-full py-4 px-6 rounded-2xl flex items-center justify-center gap-3"
                  style={{
                    background: 'var(--accent-soft)',
                    border: '1px solid var(--accent)',
                  }}
                >
                  <span className="font-sans text-2xl tracking-[0.35em] font-semibold"
                        style={{ color: 'var(--primary)' }}
                        data-testid={`voucher-code-${v.id}`}>
                    {revealedCode}
                  </span>
                </div>
                <div className="flex gap-2 w-full">
                  <button
                    onClick={copy}
                    data-testid={`voucher-copy-${v.id}`}
                    className="flex-1 py-3 rounded-xl font-sans text-sm font-medium
                               transition-transform active:scale-[0.98]
                               inline-flex items-center justify-center gap-2"
                    style={{
                      background: copied ? '#065F46' : 'var(--primary)',
                      color: 'var(--on-primary)',
                    }}
                  >
                    <AnimatePresence mode="wait">
                      {copied ? (
                        <motion.span key="ok"
                                      initial={{ scale: 0.6, opacity: 0 }}
                                      animate={{ scale: 1, opacity: 1 }}
                                      exit={{ opacity: 0 }}
                                      className="inline-flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4" /> Copied
                        </motion.span>
                      ) : (
                        <motion.span key="copy"
                                      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                                      className="inline-flex items-center gap-2">
                          <Copy className="w-4 h-4" /> Copy Code
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </button>
                  {onShare && (
                    <button
                      onClick={() => onShare(v, revealedCode)}
                      data-testid={`voucher-share-${v.id}`}
                      className="py-3 px-4 rounded-xl border font-sans text-sm font-medium
                                 transition-colors inline-flex items-center gap-2"
                      style={{
                        color: 'var(--ink)',
                        borderColor: 'var(--border-default)',
                        background: '#fff',
                      }}
                    >
                      <Share2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="hidden"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                {canReveal ? (
                  <button
                    onClick={() => onReveal(v)}
                    disabled={revealBusy}
                    data-testid={`voucher-reveal-${v.id}`}
                    className="w-full py-3.5 rounded-xl font-sans text-sm font-semibold
                               transition-transform active:scale-[0.98] disabled:opacity-60
                               inline-flex items-center justify-center gap-2"
                    style={{ background: 'var(--primary)', color: 'var(--on-primary)' }}
                  >
                    <Lock className="w-4 h-4" />
                    {revealBusy ? 'Revealing…' : 'Reveal Redemption Code'}
                  </button>
                ) : v.status === 'redeemed' ? (
                  <div className="text-center py-3 font-sans text-sm"
                       style={{ color: 'var(--ink-muted)' }}>
                    Enjoyed on {String(v.redeemed_at || '').slice(0, 10) || 'a previous visit'}
                  </div>
                ) : v.status === 'expired' ? (
                  <div className="text-center py-3 font-sans text-sm"
                       style={{ color: 'var(--ink-muted)' }}>
                    This privilege has lapsed
                  </div>
                ) : (
                  <div className="text-center py-3 font-sans text-sm"
                       style={{ color: 'var(--ink-muted)' }}>
                    Held for redemption
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.article>
  );
}

/* ============================================================
   ITER294 — Brand strip header for the Home tab.
   Slim, embedded band with logo + brand + tagline.
   ============================================================ */
export function BrandStripHeader({ outletName, programName, logoUrl,
                                   tagline, city }) {
  return (
    <div className="relative overflow-hidden"
         data-testid="brand-strip-header"
         style={{ background: 'var(--gradient-hero)' }}>
      {/* Watermark pattern */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none"
           viewBox="0 0 400 120" preserveAspectRatio="xMidYMid slice"
           aria-hidden="true">
        <defs>
          <pattern id="brand-strip-pat" x="0" y="0" width="24" height="24"
                    patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="1" fill="var(--accent)" opacity="0.28" />
          </pattern>
        </defs>
        <rect width="400" height="120" fill="url(#brand-strip-pat)" />
      </svg>

      <div className="relative flex items-center gap-4 px-6 py-5">
        {logoUrl ? (
          <div className="w-14 h-14 rounded-2xl overflow-hidden bg-white flex-shrink-0"
               style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.25)' }}>
            <img src={logoUrl} alt={outletName} className="w-full h-full object-cover" />
          </div>
        ) : (
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
               style={{ background: 'rgba(255,255,255,0.14)',
                        border: '1px solid rgba(255,255,255,0.20)',
                        backdropFilter: 'blur(6px)' }}>
            <Sparkles className="w-6 h-6" style={{ color: 'var(--accent)' }} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.22em] font-semibold"
               style={{ color: 'var(--accent)' }}>
            {programName || 'Privilege Membership'}
          </div>
          <div className="font-serif text-2xl leading-tight truncate"
               style={{ color: 'var(--on-primary)' }}>
            {outletName}
          </div>
          {(city || tagline) && (
            <div className="text-xs mt-0.5 truncate"
                 style={{ color: 'rgba(255,255,255,0.75)' }}>
              {tagline || city}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


/* ============================================================
   Bottom navigation
   ============================================================ */
export function BottomNav({ active, onNavigate }) {
  const items = [
    { id: 'home',      icon: HomeIcon, label: 'Home' },
    { id: 'vouchers',  icon: Ticket,   label: 'Privileges' },
    { id: 'qr',        icon: QrCode,   label: 'Card' },
    { id: 'history',   icon: Clock,    label: 'History' },
    { id: 'terms',     icon: FileText, label: 'Terms' },
    { id: 'profile',   icon: User,     label: 'Profile' },
  ];
  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-40"
      style={{
        background: 'rgba(11, 28, 61, 0.92)',
        backdropFilter: 'blur(14px)',
        borderTop: '1px solid rgba(255,255,255,0.10)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
      data-testid="loyalty-bottom-nav"
    >
      <div className="max-w-[480px] mx-auto flex justify-around items-center h-[72px] px-4">
        {items.map((it) => {
          const isActive = it.id === active;
          const Icon = it.icon;
          return (
            <button
              key={it.id}
              onClick={() => onNavigate(it.id)}
              data-testid={`nav-${it.id}`}
              className="relative flex flex-col items-center justify-center gap-1
                         min-w-[56px] min-h-[44px] transition-colors"
              style={{
                color: isActive ? 'var(--gold)' : 'rgba(255,255,255,0.55)',
              }}
            >
              {isActive && (
                <motion.span
                  layoutId="nav-indicator"
                  className="absolute -top-[1px] w-8 h-[3px] rounded-b-full"
                  style={{ background: 'var(--gold)' }}
                  transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                />
              )}
              <Icon className="w-[22px] h-[22px]" strokeWidth={isActive ? 2.2 : 1.6} />
              <span className="text-[10px] font-medium tracking-wide">{it.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}

/* ============================================================
   Empty state
   ============================================================ */
export function EmptyState({ title, description, icon: Icon = Ticket, testId }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-6 text-center"
         data-testid={testId}>
      <div className="w-16 h-16 rounded-full flex items-center justify-center mb-5"
           style={{ background: 'var(--surface)', border: '1px solid var(--border-default)' }}>
        <Icon className="w-6 h-6" style={{ color: 'var(--ink-muted)' }} />
      </div>
      <div className="font-serif text-xl mb-2" style={{ color: 'var(--ink)' }}>{title}</div>
      <div className="font-sans text-sm max-w-[280px]"
           style={{ color: 'var(--ink-muted)' }}>{description}</div>
    </div>
  );
}
